import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  Icon,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, {
  type ElementType,
  ReactElement,
  useCallback,
  useMemo,
} from "react";
import { HiOutlineDocumentChartBar } from "react-icons/hi2";
import { LuCompass } from "react-icons/lu";
import { RiGroupLine } from "react-icons/ri";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { formatDate } from "../../../../utils/datetime";
import { CustomInsightsReportFragment } from "../../../graphql";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import { useSavedReports } from "./SavedReportsContext";

export const BH_LIBRARY_TEMPLATES = [
  {
    title: "Interview Guide Coverage",
    description:
      "How well are interviewers following the interview guide? Which questions are being skipped?",
    output: "Coverage % + gap analysis + interviewer breakdown",
    skillName: "interview_guide_coverage",
  },
  {
    title: "Company Pitch Themes",
    description:
      "How is the company being pitched to candidates? What are the key themes?",
    output: "Theme clusters + representative quotes + trend",
    skillName: "company_pitch_themes",
  },
  {
    title: "Candidate Questions",
    description:
      "What questions are candidates asking? What aren't we answering?",
    output: "Ranked question themes + frequency + gaps",
    skillName: "candidate_questions",
  },
  {
    title: "Job Description vs Interviews Alignment",
    description:
      "To what extent does our JD align to the topics covered in interviews?",
    output: "Interviews analysis",
    skillName: "jd_interview_alignment",
  },
  {
    title: "Compensation Discussion Analysis",
    description:
      "How are comp discussions going? What are candidates pushing on?",
    output: "Trend analysis + negotiation themes + outlier recruiters",
    skillName: "compensation_analysis",
  },
  {
    title: "Interviewer Signal Quality",
    description:
      "Which interviewers provide the strongest/weakest signal? What differentiates them?",
    output: "Interviewer ranking + behavioral patterns + coaching themes",
    skillName: "interviewer_signal_quality",
  },
];

function ReportLink({
  report,
}: {
  report: CustomInsightsReportFragment;
}): ReactElement {
  const location = useLocation();
  const reportPath = `/insights/custom/reports/${report.id}`;
  const isActive = location.pathname === reportPath;

  return (
    <NavLink key={report.id} to={reportPath}>
      <Box
        _hover={{ bg: isActive ? "blue.50" : "gray.100" }}
        borderRadius="6px"
        px="8px"
        py="6px"
        display="block"
        textDecoration="none"
        bg={isActive ? "blue.50" : undefined}
      >
        <Text
          fontSize="14px"
          lineHeight="20px"
          fontWeight="medium"
          color={isActive ? "blue.600" : "gray.800"}
          noOfLines={1}
        >
          {report.title}
        </Text>
        <Text
          fontSize="12px"
          lineHeight="16px"
          color={isActive ? "blue.400" : "gray.400"}
          mt="2px"
        >
          {formatDate(report.createdAt, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
          {report.creator?.fullName ? ` · ${report.creator.fullName}` : ""}
        </Text>
      </Box>
    </NavLink>
  );
}

function ReportsSection({
  label,
  reports,
  loading,
  emptyText,
  icon,
}: {
  label: string;
  reports: CustomInsightsReportFragment[];
  loading: boolean;
  emptyText: string;
  icon: ElementType;
}): ReactElement {
  return (
    <AccordionItem border="0">
      <AccordionButton
        px="8px"
        py="10px"
        _hover={{ bg: "transparent" }}
        gap="8px"
        fontWeight="normal"
      >
        <Icon as={icon} boxSize="16px" color="gray.600" />
        <Text
          flex={1}
          textAlign="left"
          fontSize="14px"
          lineHeight="20px"
          fontWeight="normal"
          color="gray.600"
          noOfLines={1}
        >
          {label}
        </Text>
        <AccordionIcon boxSize="16px" color="gray.600" />
      </AccordionButton>
      <AccordionPanel px={0} py={0}>
        <VStack spacing={0} align="stretch" pl="24px">
          {loading && (
            <Flex py="8px" justify="center">
              <Spinner size="xs" color="gray.800" />
            </Flex>
          )}
          {reports.length > 0 ? (
            reports.map((report) => (
              <ReportLink key={report.id} report={report} />
            ))
          ) : (
            <Text fontSize="12px" lineHeight="16px" color="gray.400" py="4px">
              {emptyText}
            </Text>
          )}
        </VStack>
      </AccordionPanel>
    </AccordionItem>
  );
}

function SavedReportsContent(): ReactElement {
  const currentUser = useCurrentUser();
  const { reports, loading } = useSavedReports();

  const myReports = useMemo(
    () => reports.filter((r) => r.creator?.id === currentUser.id),
    [reports, currentUser.id]
  );
  const sharedReports = useMemo(
    () => reports.filter((r) => r.creator?.id !== currentUser.id),
    [reports, currentUser.id]
  );

  return (
    <>
      <ReportsSection
        label="Saved reports"
        reports={myReports}
        loading={loading}
        emptyText="No saved reports yet"
        icon={HiOutlineDocumentChartBar}
      />
      <ReportsSection
        label="Shared with me"
        reports={sharedReports}
        loading={loading}
        emptyText="No shared reports yet"
        icon={RiGroupLine}
      />
    </>
  );
}

function CustomInsightsSidebar({
  isExpanded,
}: {
  isExpanded: boolean;
}): ReactElement | null {
  const { newExploration } = useCustomInsightsChat();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewExplorationActive = location.pathname === "/insights/custom";

  const handleNewExploration = useCallback(() => {
    newExploration();
    navigate("/insights/custom");
  }, [newExploration, navigate]);

  if (!isExpanded) return null;

  return (
    <Flex
      w="200px"
      minW="200px"
      bg="gray.50"
      borderRight="1px solid"
      borderColor="gray.200"
      flexDir="column"
      pt="12px"
      pb="24px"
      px="12px"
      overflow="auto"
    >
      <Box
        as="button"
        w="100%"
        bg={isNewExplorationActive ? "gray.200" : "gray.100"}
        borderRadius="6px"
        px="8px"
        py="10px"
        display="flex"
        alignItems="center"
        gap="8px"
        cursor="pointer"
        _hover={{ bg: "gray.200" }}
        onClick={handleNewExploration}
        flexShrink={0}
      >
        <Icon as={LuCompass} boxSize="16px" color="gray.800" />
        <Text
          fontSize="14px"
          lineHeight="20px"
          fontWeight="normal"
          color="gray.800"
          noOfLines={1}
          flex={1}
          textAlign="left"
        >
          New exploration
        </Text>
      </Box>

      <Divider my="8px" borderColor="gray.200" />

      <Accordion allowMultiple defaultIndex={[0]}>
        <SavedReportsContent />
      </Accordion>
    </Flex>
  );
}

export default CustomInsightsSidebar;
